import { Dispatch } from 'redux';

import {
  Building,
  BuildingsParams,
  getBuilding,
  getBuildings,
  getCities,
  getServices,
  Service,
  StateCities,
} from '../../services/buildings';
import { captureException } from '../../services/sentry';
import { getUnits, Unit } from '../../services/units';
import {
  ActionInterface,
  dispatcher,
  generateActions,
} from '../../utils/redux';
import { toQueryString } from '../../utils/url';
import { SearchActions } from '../search/actions';

export enum BuildingActions {
  getBuildingStart = '[BUILDING]:GET_BUILDING:START',
  getBuildingSuccess = '[BUILDING]:GET_BUILDING:SUCCESS',
  getBuildingError = '[BUILDING]:GET_BUILDING:ERROR',
  getServicesStart = '[BUILDING]:GET_SERVICES:START',
  getServicesSuccess = '[BUILDING]:GET_SERVICES:SUCCESS',
  getServicesError = '[BUILDING]:GET_SERVICES:ERROR',
  getBuildingUnitsStart = '[BUILDING]:GET_BUILDING_UNITS:START',
  getBuildingUnitsSuccess = '[BUILDING]:GET_BUILDING_UNITS:SUCCESS',
  getBuildingUnitsError = '[BUILDING]:GET_BUILDING_UNITS:ERROR',
  getCitiesStart = '[BUILDING]:GET_CITIES:START',
  getCitiesSuccess = '[BUILDING]:GET_CITIES:SUCCESS',
  getCitiesError = '[BUILDING]:GET_CITIES:ERROR',
  getBuildingsStart = '[BUILDING]:GET_BUILDINGS:START',
  getBuildingsSuccess = '[BUILDING]:GET_BUILDINGS:SUCCESS',
  getBuildingsError = '[BUILDING]:GET_BUILDINGS:ERROR',
  setBuildings = '[BUILDING]:SET_BUILDINGS',
}

const buildingActionsMap: {
  [key: string]: BuildingActions;
} = BuildingActions as any;

const getBuildingsStart = (params: BuildingsParams) => {
  return {
    payload: toQueryString(params),
    type: BuildingActions.getBuildingsStart,
  };
};

export function DoGetBuildings(params: BuildingsParams) {
  const [, success, error] = generateActions<
    BuildingActions,
    { buildings: Building[]; query: string }
  >('getBuildings', buildingActionsMap);
  return async (
    d: Dispatch<ActionInterface<BuildingActions>>
  ): Promise<any> => {
    const dispatch = dispatcher(d);
    dispatch(getBuildingsStart)();
    try {
      const response = await getBuildings(params);
      dispatch(success)(response);
      return response;
    } catch (e) {
      const errors = captureException(e);
      return dispatch(error)(errors.toString());
    }
  };
}

export function DoSetResults({
  buildings,
  query,
}: {
  buildings: Building[];
  query: string;
}) {
  return async (d: Dispatch<ActionInterface<SearchActions>>) => {
    const dispatch = dispatcher(d as any);

    const actionCreator = ({
      buildings,
      query,
    }: {
      buildings: Building[];
      query: string;
    }) => ({
      payload: { buildings, query },
      type: BuildingActions.setBuildings,
    });
    dispatch(actionCreator)({ buildings, query });
  };
}

export function DoGetBuilding(id: number) {
  const [start, success, error] = generateActions<BuildingActions, Building>(
    'getBuilding',
    buildingActionsMap
  );
  return async (
    d: Dispatch<ActionInterface<BuildingActions>>
  ): Promise<any> => {
    const dispatch = dispatcher(d);
    dispatch(start)();
    try {
      const response = await getBuilding(id);
      return dispatch(success)(response);
    } catch (e) {
      const errors = captureException(e);
      return dispatch(error)(errors.toString());
    }
  };
}

export function DoGetServices(id: number) {
  const [start, success, error] = generateActions<BuildingActions, Service[]>(
    'getServices',
    buildingActionsMap
  );
  return async (
    d: Dispatch<ActionInterface<BuildingActions>>
  ): Promise<any> => {
    const dispatch = dispatcher(d);
    dispatch(start)();
    try {
      const response = await getServices(id);
      return dispatch(success)(response);
    } catch (e) {
      const errors = captureException(e);
      return dispatch(error)(errors.toString());
    }
  };
}

export function DoGetCities() {
  const [start, success, error] = generateActions<BuildingActions, StateCities>(
    'getCities',
    buildingActionsMap
  );
  return async (
    d: Dispatch<ActionInterface<BuildingActions>>
  ): Promise<any> => {
    const dispatch = dispatcher(d);
    dispatch(start)();
    try {
      const response = await getCities();
      return dispatch(success)(response);
    } catch (e) {
      const errors = captureException(e);
      return dispatch(error)(errors.toString());
    }
  };
}

export function DoGetBuildingUnits(id: number) {
  const [start, success, error] = generateActions<BuildingActions, Unit[]>(
    'getBuildingUnits',
    buildingActionsMap
  );
  return async (
    d: Dispatch<ActionInterface<BuildingActions>>
  ): Promise<any> => {
    const dispatch = dispatcher(d);
    dispatch(start)();
    try {
      const response = await getUnits({ building_id: id });
      return dispatch(success)(response);
    } catch (e) {
      const errors = captureException(e);
      return dispatch(error)(errors.toString());
    }
  };
}
